import type { ReactNode } from 'react';
import { memo, useState } from 'react';

import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import cn from 'classnames';

import { COPY_TEXT_TIMEOUT } from '../../../constants/copy';
import { useCopyToClipboard } from '../../../lib/copy-to-clipboard';

import cls from './input-text-copy.module.css';

interface InputTextCopyProps {
    value: string;
    className?: string;
    size?: 'small' | 'medium' | 'large';
    label?: string;
    pictogram?: ReactNode;
}

export const InputTextCopy = memo((props: InputTextCopyProps) => {
    const { value, className, size, label, pictogram } = props;

    const [isCopied, setIsCopied] = useState(false);

    const copyToClipboard = useCopyToClipboard(value, () => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), COPY_TEXT_TIMEOUT);
    });

    return (
        <div className={cn(className, cls.root)}>
            {label && <span className={cls.label}>{label}</span>}
            <div
                className={cn(cls.content, {
                    [cls.small]: size === 'small',
                    [cls.medium]: size === 'medium',
                    [cls.large]: size === 'large',
                })}
            >
                {pictogram && (
                    <span className={cls.pictogram}>{pictogram}</span>
                )}
                <span className={cls.value}>{value}</span>
                {isCopied ? (
                    <CheckOutlined className={cn(cls.copyIcon, cls.copied)} />
                ) : (
                    <CopyOutlined
                        className={cls.copyIcon}
                        onClick={copyToClipboard}
                    />
                )}
            </div>
        </div>
    );
});

InputTextCopy.displayName = 'InputTextCopy';
